export const ARTICLES = [
  {
    id: "five-factors",
    category: "Score Mechanics",
    readTime: "6 min",
    title: "The Five Factors That Shape Your Score",
    excerpt:
      "Payment history, utilization, age of credit, mix, and inquiries — the weighted recipe behind every score, explained without jargon.",
    body: [
      "Every major scoring model is built on five pillars. Payment history carries the most weight — roughly a third of your score — because lenders care most about one question: do you pay on time?",
      "Second comes utilization: how much of your available revolving credit you're using. Keeping reported balances low relative to limits is the fastest lever most people can pull.",
      "The remaining three — length of history, credit mix, and new inquiries — reward patience. Old accounts are assets. A blend of installment and revolving accounts shows versatility. And every hard inquiry is a small, temporary tax on your profile.",
      "Once you see the recipe, the system stops feeling like a judgment and starts feeling like a game with published rules. That shift in posture is where elevation begins.",
    ],
  },
  {
    id: "read-your-report",
    category: "Report Literacy",
    readTime: "8 min",
    title: "How to Read Your Credit Report Like an Analyst",
    excerpt:
      "Personal data, tradelines, public records, inquiries — a guided tour of the document that quietly decides your interest rates.",
    body: [
      "Your credit report is not one document — it's three, one per bureau, and they rarely match. Pulling all three is step one of any serious elevation plan.",
      "Start with personal information. Misspelled names, old addresses, and wrong employers seem harmless, but they can merge other people's data into your file.",
      "Next, the tradelines: every account, its limit, balance, status, and payment grid. Underwriters scan for late-payment markers first, then scan utilization patterns. You should too.",
      "Finally, inquiries and public records. Hard pulls older than two years fall off; collections and bankruptcies follow their own clocks. Knowing what expires — and when — turns waiting into strategy.",
    ],
  },
  {
    id: "utilization-lever",
    category: "Score Mechanics",
    readTime: "5 min",
    title: "Utilization: The Quiet Lever",
    excerpt:
      "Why a card you never max out can still hurt you — and the reporting-date trick that fixes it in one cycle.",
    body: [
      "Utilization is measured at the moment your issuer reports to the bureaus — usually your statement closing date, not your due date. Pay in full every month and you can still show a high balance.",
      "The fix is timing: pay down your balance before the statement closes, and the bureaus see a smaller number. Same spending, better data.",
      "Both per-card and overall utilization matter. One maxed card can drag an otherwise spotless profile.",
      "Conventional wisdom says stay under 30%. The clients who elevate fastest aim for single digits — not as a lifestyle of deprivation, but as a reporting strategy.",
    ],
  },
  {
    id: "dispute-rights",
    category: "Your Rights",
    readTime: "7 min",
    title: "Accuracy Is Your Right: A Calm Guide to Disputes",
    excerpt:
      "The FCRA gives you the right to an accurate report. Here's how to exercise it methodically — and what it can and cannot do.",
    body: [
      "The Fair Credit Reporting Act requires bureaus and furnishers to report accurate, verifiable, complete information. When something on your report fails that test, you have the right to challenge it.",
      "A good dispute is specific. Identify the item, state exactly what is wrong, attach evidence, and send it through channels that create a paper trail.",
      "Bureaus generally have 30 days to investigate. If the furnisher can't verify the item, it must be corrected or removed. That's the law working as designed — not a loophole.",
      "What disputes cannot do is erase accurate history. Education-first firms are honest about this: disputes clean up errors, while behavior rebuilds the rest.",
    ],
  },
  {
    id: "business-credit-zero",
    category: "Business Funding",
    readTime: "9 min",
    title: "Building Business Credit From Zero",
    excerpt:
      "EIN, D-U-N-S, vendor tiers, and the sequence that separates a fundable company from a personal guarantee.",
    body: [
      "Business credit is a parallel system with its own bureaus, scores, and rules — and most founders don't know it exists until a lender asks about it.",
      "The foundation is boring but non-negotiable: a registered entity, an EIN, a business bank account, a dedicated phone line, and consistent public information everywhere.",
      "Then come vendor tradelines — net-30 accounts that report to business bureaus. Three to five reporting vendors establish a file; managed well, they build the payment history that unlocks revolving credit.",
      "The destination is separation: your company borrowing on its own strength, without every loan leaning on your personal profile. That's funding readiness.",
    ],
  },
  {
    id: "ninety-day-plan",
    category: "Action Plans",
    readTime: "6 min",
    title: "The 90-Day Elevation Plan",
    excerpt:
      "A realistic quarter: what to do in weeks one through twelve when you want measurable movement by day ninety.",
    body: [
      "Days 1–15: pull all three reports, audit personal data, and map every tradeline. You cannot elevate what you haven't measured.",
      "Days 16–45: attack utilization with reporting-date payments, set autopay minimums on everything, and file specific, evidence-backed disputes for genuine errors.",
      "Days 46–75: hold steady. No new accounts, no new inquiries. Let the corrections post and the balances report low for a second consecutive cycle.",
      "Days 76–90: re-pull, compare, and re-sequence. Momentum in credit is measured in reporting cycles — three clean cycles change conversations with lenders.",
    ],
  },
];
